import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { twoFactorApi } from '../../services/api'
import {
  ShieldCheck,
  Smartphone,
  Mail,
  Fingerprint,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Copy,
  Check,
  ArrowRight,
  LogOut,
  AlertCircle,
  Download
} from 'lucide-react'

// Helper: converte ArrayBuffer in base64url
function arrayBufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

// Helper: verifica supporto WebAuthn
function isWebAuthnSupported() {
  return !!(
    window.PublicKeyCredential &&
    typeof window.PublicKeyCredential === 'function'
  )
}

// Helper: verifica supporto platform authenticator (TouchID, FaceID, Windows Hello)
async function isPlatformAuthenticatorAvailable() {
  if (!isWebAuthnSupported()) return false
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch {
    return false
  }
}

export default function TwoFactorSetupPage() {
  const navigate = useNavigate()
  const { user, logout, checkAuth, needs2FASetup, mark2FAConfigured } = useAuth()
  
  const [step, setStep] = useState('choose') // choose, setup, verify, backup, complete
  const [selectedMethod, setSelectedMethod] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  
  // TOTP state
  const [totpData, setTotpData] = useState(null)
  const [totpCode, setTotpCode] = useState('')
  
  // Email OTP state
  const [emailEnabled, setEmailEnabled] = useState(false)
  
  // Backup codes state
  const [backupCodes, setBackupCodes] = useState([])
  const [copiedCodes, setCopiedCodes] = useState(false)
  const [confirmedBackup, setConfirmedBackup] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)

  // WebAuthn state
  const [webAuthnSupported, setWebAuthnSupported] = useState(false)
  const [platformAuthAvailable, setPlatformAuthAvailable] = useState(false)
  const [passkeyName, setPasskeyName] = useState('')

  // Check WebAuthn support on mount
  useEffect(() => {
    const checkWebAuthn = async () => {
      setWebAuthnSupported(isWebAuthnSupported())
      const platformAvailable = await isPlatformAuthenticatorAvailable()
      setPlatformAuthAvailable(platformAvailable)
    }
    checkWebAuthn()
  }, [])

  // Redirect se ruolo non supportato o se 2FA già configurato
  useEffect(() => {
    if (!user) return
    
    // Superadmin non configura 2FA qui
    if (user.role === 'superadmin') {
      navigate('/dashboard', { replace: true })
      return
    }
    
    // Coordinatore/Responsabile: 2FA obbligatorio, redirect se già configurato
    if ((user.role === 'coordinatore' || user.role === 'responsabile') && !needs2FASetup) {
      navigate('/dashboard', { replace: true })
      return
    }
    
    // Educatore: 2FA opzionale, può accedere per configurare volontariamente
    // Non fare redirect automatico per educatori
  }, [user, needs2FASetup, navigate])

  // === TOTP SETUP ===
  const startTotpSetup = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await twoFactorApi.setupTotp()
      
      if (response.data.success) {
        setTotpData(response.data.data)
        setStep('setup')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore durante la configurazione')
    } finally {
      setLoading(false)
    }
  }

  const verifyTotp = async () => {
    if (totpCode.length !== 6) {
      setError('Inserisci un codice a 6 cifre')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      const response = await twoFactorApi.verifyTotp(totpCode)
      
      if (response.data.success) {
        setBackupCodes(response.data.data.backupCodes || [])
        setStep('backup')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Codice non valido')
      setTotpCode('')
    } finally {
      setLoading(false)
    }
  }

  // === EMAIL OTP SETUP ===
  const enableEmailOtp = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await twoFactorApi.enableEmail()
      
      if (response.data.success) {
        setEmailEnabled(true)
        setBackupCodes(response.data.data.backupCodes || [])
        setStep('backup')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore durante l\'attivazione')
    } finally {
      setLoading(false)
    }
  }

  // === WEBAUTHN/PASSKEY SETUP ===
  const startPasskeySetup = async () => {
    if (!webAuthnSupported) {
      setError('Il tuo browser non supporta le Passkey/WebAuthn')
      return
    }

    try {
      setLoading(true)
      setError(null)
      setSelectedMethod('webauthn')

      // 1. Ottieni opzioni dal server
      const optionsResponse = await twoFactorApi.webauthnRegisterOptions()
      
      if (!optionsResponse.data.success) {
        throw new Error(optionsResponse.data.message || 'Errore ottenimento opzioni')
      }

      const options = optionsResponse.data.data

      // 2. Converti challenge e user.id da base64url a Uint8Array
      const publicKeyOptions = {
        ...options,
        challenge: Uint8Array.from(atob(options.challenge.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)),
        user: {
          ...options.user,
          id: Uint8Array.from(atob(options.user.id.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0))
        }
      }

      // Converti excludeCredentials se presenti
      if (options.excludeCredentials) {
        publicKeyOptions.excludeCredentials = options.excludeCredentials.map(cred => ({
          ...cred,
          id: Uint8Array.from(atob(cred.id.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0))
        }))
      }

      // 3. Chiedi al browser di creare la credenziale (mostra prompt biometrico)
      const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions
      })

      if (!credential) {
        throw new Error('Creazione passkey annullata')
      }

      // 4. Prepara risposta per il server
      const attestationResponse = {
        id: credential.id,
        rawId: arrayBufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: arrayBufferToBase64url(credential.response.clientDataJSON),
          attestationObject: arrayBufferToBase64url(credential.response.attestationObject),
          transports: credential.response.getTransports ? credential.response.getTransports() : ['internal']
        },
        name: passkeyName || `Passkey ${new Date().toLocaleDateString('it-IT')}`
      }

      // 5. Verifica sul server
      const verifyResponse = await twoFactorApi.webauthnRegisterVerify(attestationResponse)

      if (verifyResponse.data.success) {
        setBackupCodes(verifyResponse.data.data.backupCodes || [])
        setStep('backup')
      } else {
        throw new Error(verifyResponse.data.message || 'Verifica passkey fallita')
      }

    } catch (err) {
      console.error('Passkey setup error:', err)
      
      // Gestisci errori specifici WebAuthn
      if (err.name === 'NotAllowedError') {
        setError('Registrazione annullata o timeout. Riprova.')
      } else if (err.name === 'InvalidStateError') {
        setError('Questa passkey è già registrata.')
      } else if (err.name === 'NotSupportedError') {
        setError('Il tuo dispositivo non supporta questo tipo di autenticazione.')
      } else {
        setError(err.response?.data?.message || err.message || 'Errore durante la configurazione della passkey')
      }
      
      setSelectedMethod(null)
    } finally {
      setLoading(false)
    }
  }

  // === BACKUP CODES ===
  const copyBackupCodes = async () => {
    const codesText = backupCodes.join('\n')
    try {
      await navigator.clipboard.writeText(codesText)
      setCopiedCodes(true)
      setTimeout(() => setCopiedCodes(false), 3000)
    } catch (err) {
      // Fallback per browser che non supportano clipboard API
      const textarea = document.createElement('textarea')
      textarea.value = codesText
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopiedCodes(true)
      setTimeout(() => setCopiedCodes(false), 3000)
    }
  }

  const downloadBackupCodesPdf = async () => {
    if (backupCodes.length === 0) return
    
    try {
      setDownloadingPdf(true)
      setError(null)
      
      const response = await twoFactorApi.downloadBackupCodesPdf(backupCodes)
      
      // Crea blob e scarica
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `codici-backup-2fa-${new Date().toISOString().split('T')[0]}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
    } catch (err) {
      console.error('Download PDF error:', err)
      setError('Errore durante il download del PDF')
    } finally {
      setDownloadingPdf(false)
    }
  }

  const completeSetup = async () => {
    if (!confirmedBackup) {
      setError('Conferma di aver salvato i codici di backup')
      return
    }
    
    // Marca 2FA come configurato (senza fare chiamate API)
    mark2FAConfigured()
    setStep('complete')
    
    // Redirect dopo 2 secondi
    setTimeout(() => {
      navigate('/dashboard', { replace: true })
    }, 2000)
  }

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  // Se user non è ancora caricato, mostra loading
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  // === RENDER ===
  
  // Step 1: Scelta metodo
  if (step === 'choose') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 p-4">
        <div className="w-full max-w-lg">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Configura la sicurezza del tuo account
              </h1>
              <p className="text-gray-500 dark:text-gray-400 mt-2">
                {user?.role === 'educatore' 
                  ? "Proteggi il tuo account con l'autenticazione a due fattori."
                  : "Devi configurare l'autenticazione a due fattori per proteggere i dati sensibili."
                }
              </p>
            </div>

            {/* Alert */}
            <div className={`${user?.role === 'educatore' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'} border rounded-lg p-4 mb-6`}>
              <div className="flex gap-3">
                <AlertTriangle className={`h-5 w-5 ${user?.role === 'educatore' ? 'text-blue-600' : 'text-amber-600'} flex-shrink-0 mt-0.5`} />
                <div>
                  <p className={`text-sm ${user?.role === 'educatore' ? 'text-blue-800 dark:text-blue-200' : 'text-amber-800 dark:text-amber-200'}`}>
                    {user?.role === 'educatore' ? (
                      <><strong>Consigliato:</strong> Il 2FA aggiunge un ulteriore livello di sicurezza al tuo account.</>
                    ) : (
                      <><strong>Obbligatorio:</strong> Il 2FA protegge i dati sensibili dei minori e garantisce la conformità GDPR.</>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Metodi disponibili */}
            <div className="space-y-3">
              <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Scegli un metodo di autenticazione:
              </h2>

              {/* TOTP */}
              <button
                onClick={() => {
                  setSelectedMethod('totp')
                  startTotpSetup()
                }}
                disabled={loading}
                className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors text-left"
              >
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Smartphone className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    App Authenticator
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Usa Google Authenticator, Authy o simili
                  </p>
                </div>
                <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full">
                  Consigliato
                </span>
              </button>

              {/* Email OTP */}
              <button
                onClick={() => {
                  setSelectedMethod('email')
                  enableEmailOtp()
                }}
                disabled={loading}
                className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors text-left"
              >
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Codice via Email
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Ricevi un codice sulla tua email ad ogni accesso
                  </p>
                </div>
              </button>

              {/* Passkey/WebAuthn */}
              <button
                onClick={startPasskeySetup}
                disabled={loading || !webAuthnSupported}
                className={`w-full flex items-center gap-4 p-4 border-2 rounded-xl transition-colors text-left ${
                  webAuthnSupported
                    ? 'border-gray-200 dark:border-gray-700 hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/10'
                    : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 opacity-60 cursor-not-allowed'
                }`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  webAuthnSupported
                    ? 'bg-purple-100 dark:bg-purple-900/30'
                    : 'bg-gray-200 dark:bg-gray-700'
                }`}>
                  <Fingerprint className={`h-6 w-6 ${
                    webAuthnSupported
                      ? 'text-purple-600 dark:text-purple-400'
                      : 'text-gray-400'
                  }`} />
                </div>
                <div className="flex-1">
                  <h3 className={`font-semibold ${
                    webAuthnSupported
                      ? 'text-gray-900 dark:text-white'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    Passkey / Biometrico
                  </h3>
                  <p className={`text-sm ${
                    webAuthnSupported
                      ? 'text-gray-500 dark:text-gray-400'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}>
                    {webAuthnSupported
                      ? 'Impronta digitale, Face ID o chiave di sicurezza'
                      : 'Non supportato dal tuo browser'
                    }
                  </p>
                </div>
                {webAuthnSupported && platformAuthAvailable && (
                  <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs rounded-full">
                    Disponibile
                  </span>
                )}
              </button>

              {/* Input nome passkey (mostrato solo se WebAuthn selezionato ma non ancora avviato) */}
              {selectedMethod === 'webauthn' && !loading && (
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nome della passkey (opzionale)
                  </label>
                  <input
                    type="text"
                    value={passkeyName}
                    onChange={(e) => setPasskeyName(e.target.value)}
                    placeholder="es. MacBook Pro, iPhone"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              )}
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
                <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {loading && (
              <div className="mt-4 flex items-center justify-center gap-2 text-primary-600">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>
                  {selectedMethod === 'webauthn' 
                    ? 'Segui le istruzioni sul tuo dispositivo...'
                    : 'Configurazione in corso...'
                  }
                </span>
              </div>
            )}

            {/* Logout option */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 py-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <LogOut className="h-4 w-4" />
                <span>Esci e accedi con un altro account</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Step 2: Setup TOTP
  if (step === 'setup' && selectedMethod === 'totp' && totpData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 p-4">
        <div className="w-full max-w-lg">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Configura l'app Authenticator
              </h1>
              <p className="text-gray-500 dark:text-gray-400 mt-2">
                Scansiona il QR code con la tua app authenticator
              </p>
            </div>

            {/* QR Code */}
            <div className="flex justify-center mb-6">
              <div className="bg-white p-4 rounded-lg shadow-inner">
                <img 
                  src={totpData.qrCode} 
                  alt="QR Code" 
                  className="w-48 h-48"
                />
              </div>
            </div>

            {/* Secret manuale */}
            <div className="mb-6">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-2">
                Oppure inserisci manualmente questo codice:
              </p>
              <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 text-center">
                <code className="text-sm font-mono text-gray-800 dark:text-gray-200 break-all">
                  {totpData.secret}
                </code>
              </div>
            </div>

            {/* Verifica */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Inserisci il codice a 6 cifre dall'app:
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
              />

              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={verifyTotp}
                disabled={loading || totpCode.length !== 6}
                className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    Verifica e attiva
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>

              <button
                onClick={() => {
                  setStep('choose')
                  setTotpData(null)
                  setTotpCode('')
                  setError(null)
                }}
                className="w-full py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                ← Torna indietro
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Step 3: Backup codes
  if (step === 'backup') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 p-4">
        <div className="w-full max-w-lg">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                2FA Attivato!
              </h1>
              <p className="text-gray-500 dark:text-gray-400 mt-2">
                Salva questi codici di backup in un luogo sicuro
              </p>
            </div>

            {/* Warning */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>Importante:</strong> Questi codici ti permettono di accedere se perdi l'accesso al tuo metodo 2FA. Ogni codice può essere usato una sola volta.
                </p>
              </div>
            </div>

            {/* Codici */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, index) => (
                  <div 
                    key={index}
                    className="font-mono text-sm text-center py-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600"
                  >
                    {code}
                  </div>
                ))}
              </div>
            </div>

            {/* Azioni: Copia e Scarica PDF */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={copyBackupCodes}
                className="py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center gap-2"
              >
                {copiedCodes ? (
                  <>
                    <Check className="h-4 w-4 text-green-600" />
                    <span className="text-green-600">Copiati!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    <span>Copia codici</span>
                  </>
                )}
              </button>

              <button
                onClick={downloadBackupCodesPdf}
                disabled={downloadingPdf}
                className="py-2 border border-primary-300 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/40 flex items-center justify-center gap-2 text-primary-700 dark:text-primary-300 disabled:opacity-50"
              >
                {downloadingPdf ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Download...</span>
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    <span>Scarica PDF</span>
                  </>
                )}
              </button>
            </div>

            {/* Conferma */}
            <div className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmedBackup}
                  onChange={(e) => setConfirmedBackup(e.target.checked)}
                  className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Confermo di aver salvato i codici di backup in un luogo sicuro
                </span>
              </label>

              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={completeSetup}
                disabled={!confirmedBackup}
                className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                Completa configurazione
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Step 4: Completato
  if (step === 'complete') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 p-4">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Account protetto!
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              L'autenticazione a due fattori è ora attiva sul tuo account.
            </p>
            <div className="flex items-center justify-center gap-2 text-primary-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Reindirizzamento...</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Fallback
  return null
}
